// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { getCookie } from "@insite/client-framework/Common/Cookies";
import {
    ApiHandler,
    createHandlerChainRunner,
    HasOnError,
    HasOnSuccess,
} from "@insite/client-framework/HandlerCreator";
import loadProductInfoList from "@insite/client-framework/Store/Components/ProductInfoList/Handlers/LoadProductInfoList";
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import setReorderPadFilterQuery from "@keller/App/Handlers/ReorderPad/SetReorderPadFilterQuery";
import { loadReorderPadApiParameter, ReorderPadApiParameter } from "@keller/App/Services/ReorderPadService";
import { ReorderPadApiModal, ReorderPadProductFilters } from "@keller/App/Store/Reducers/xReorderPad";
import qs from "qs";

type HandlerType = ApiHandler<ReorderPadApiParameter & HasOnSuccess & HasOnError<string>, ReorderPadApiModal>;

export const DispatchBeginLoadReorderPad: HandlerType = props => {
    props.dispatch({
        type: "xReorderPad/BeginLoadReorderPad" as any,
    });
};

export const ParseQueryParameter: HandlerType = props => {
    const queryString = getLocation(props.getState()).search;
    const parsedQuery = qs.parse(queryString.startsWith("?") ? queryString.substr(1) : queryString);

    const splitCommaSeparated = (value?: string | string[] | null) =>
        typeof value === "string" ? value.split(",") : undefined;
    const { page, sort, searchWithin, fromDate, toDate } = parsedQuery as ReorderPadProductFilters;

    let pageSize = parsedQuery.pageSize;

    if (!pageSize) {
        pageSize = getCookie("ReorderPadProductList-PageSize");
    }

    const productFilters: ReorderPadProductFilters = {
        page: page ? parseInt(page.toString(), 10) : undefined,
        pageSize: pageSize ? parseInt(pageSize as string, 10) : undefined,
        sort,
        searchWithin: splitCommaSeparated(searchWithin),
        fromDate,
        toDate,
    };

    props.dispatch({
        type: "xReorderPad/SetProductFilters" as any,
        result: productFilters,
    });
};

export const DispatchSetFilterQuery: HandlerType = props => {
    props.dispatch(setReorderPadFilterQuery());
};

export const PopulateApiParameter: HandlerType = props => {
    const { onSuccess, onError, ...apiParameter } = props.parameter;
    props.apiParameter = apiParameter;
};

export const RequestDataFromApi: HandlerType = async props => {
    try {
        props.apiResult = await loadReorderPadApiParameter(props.apiParameter);
    } catch (error) {
        const failedMessage =
            error.errorMessage || "Please try again and contact support if you continue to have issues.";
        props.parameter.onError?.(failedMessage);
        props.dispatch({ type: "xReorderPad/FailedToLoadReorderPad", failedMessage } as any);
        return false;
    }
};

export const LoadProductsById: HandlerType = props => {
    if (!props.apiResult.items || props.apiResult.items?.length === 0) {
        return;
    }

    const productIds = props.apiResult.items.map(item => item.productERPNumber);
    props.dispatch(
        loadProductInfoList({
            id: props.parameter.id,
            getProductCollectionParameter: { productNumbers: productIds, pageSize: 9999 } as any,
        }),
    );
};
export const DispatchCompleteLoadReorderPad: HandlerType = props => {
    props.dispatch({
        type: "xReorderPad/CompleteLoadReorderPad",
        data: props.apiResult,
    } as any);
};

export const ExecuteOnSuccessCallback: HandlerType = props => {
    props.parameter.onSuccess?.();
};

export const chain = [
    DispatchBeginLoadReorderPad,
    ParseQueryParameter,
    DispatchSetFilterQuery,
    PopulateApiParameter,
    RequestDataFromApi,
    LoadProductsById,
    DispatchCompleteLoadReorderPad,
    ExecuteOnSuccessCallback,
];

const loadReorderPad = createHandlerChainRunner(chain, "LoadReorderPad");
export default loadReorderPad;
